<template>
  <div class="flex flex-col">
    <div v-if="showTextScale" class="flex flex-col p-3 bg-gray-900 gap-3">
      <div class="flex flex-col justify-between w-full gap-4">
        <div class="flex flex-row justify-between">
          <span>Model text scale</span>
          <input
            ref="modelTextScaleInputRef"
            data-testid="model-text-scale-number-field"
            :value="computedModelTextScale"
            :defaultValue="computedModelTextScale"
            class="text-sm text-center rounded-md text-gray-750 focus:outline-none"
            type="number"
            min="0.1"
            max="10"
            @keyup.enter="computedModelTextScale = (<HTMLInputElement>$event.target).value"
            @blur="handleInputBlur"
          />
        </div>
        <div class="flex flex-1 py-2">
          <EditorUiRangeSlider
            :min="0.1"
            :max="10"
            :step="0.01"
            :value="calculateInitialSliderValue(modelTextScale)"
            dark
            full-width
            :show-value-label="false"
            @input="handleModelTextScaleInput"
          />
        </div>
      </div>
      <div class="w-full">
        <button class="w-full px-3 py-1 rounded bg-copper-800 leading-5" @click="$emit('save-text-scale')">Save</button>
      </div>
    </div>
    <EditorUiSearchInput
      v-if="showSearch"
      :value="search"
      class="text-white bg-gray-700"
      :disabled="disabled"
      name="model-layer"
      data-testid="layer-list-search"
      placeholder="Search"
      @input="$emit('set-search', $event)"
    />
    <DeckGroupedLayers
      v-if="layerGroups.length"
      ref="groupedLayers"
      class="flex-grow overflow-y-auto bg-gray-900"
      :layer-groups="layerGroups"
      :selected-layers="selectedLayers"
      :readonly="readonly"
      :disabled="disabled"
      :labels-manager="threeDViewer.labelsManager"
      :scoping-element="scopingElement"
      :three-d-viewer="threeDViewer"
      :selected-label-id="selectedLabelId"
      :selected-drill-layer-group="selectedDrillLayerGroup"
      @set-layer-visible="setLayerVisible"
      @layer-hover="$emit('layer-hover', $event)"
      @click="forwardClick"
      @contextmenu="forwardContextmenu"
      @collapsible-header-click="$emit('collapsible-header-click', $event)"
    />
    <div v-else class="flex-grow pt-4 text-sm font-light text-center text-gray-500 bg-gray-900">
      <XCircleIcon class="w-10 h-10 mx-auto" />
      <h4 class="mt-2 mb-0 text-lg font-light" data-testid="no-results-message">Sorry!</h4>
      <p class="m-0">No layers were found.</p>
      <p class="m-0">Please try some other keywords.</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly, ComponentPublicInstance } from 'vue'
import { XCircleIcon } from '@heroicons/vue/20/solid'
import type { DrillLabelVisibility, LabelVisibility, SceneElement, NodeVisibility } from '@/entities/model3d'
import { debounce } from '@/lib/utils'
import type { LayerGroup, UseThreeDViewer } from '~/composables/useThreeDViewer'
import type { ThreeDElement } from '~/composables/useLayerSelect'
import type { DrillDataSet } from '~/entities/drillLabel'
import { isDrillLabelVisibility, isLabelVisibility, isNodeVisibility } from '@/entities/model3d'

const props = defineProps({
  layerGroups: {
    type: Array as PropType<LayerGroup[]>,
    required: true,
  },
  search: {
    type: String,
    required: true,
  },
  selectedLayers: {
    type: Array as PropType<DeepReadonly<ThreeDElement[]>>,
    default: () => [],
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  threeDViewer: {
    type: Object as PropType<UseThreeDViewer>,
    required: true,
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
  scopingElement: {
    type: Object as PropType<SceneElement | null>,
    default: null,
  },
  showTextScale: {
    type: Boolean,
    default: false,
  },
  selectedLabelId: {
    type: String as PropType<string | null>,
    default: null,
  },
  selectedDrillLayerGroup: {
    type: Object as PropType<DrillDataSet | undefined>,
    default: undefined,
  },
})

const emit = defineEmits([
  'click',
  'collapsible-header-click',
  'contextmenu',
  'layer-hover',
  'save-text-scale',
  'set-layer-visible',
  'update-text-scale',
  'set-search',
])

const groupedLayers = ref<ComponentPublicInstance | undefined>()
const modelTextScaleInputRef = ref<HTMLInputElement | null>(null)
const modelTextScale = ref<number>(props.threeDViewer.labelsManager.globalTextScale)
const computedModelTextScale = computed({
  get: () => Number(modelTextScale.value).toFixed(2),
  set: (value: string) => {
    let floatVal = parseFloat(value)

    if (floatVal > 10.0) {
      modelTextScale.value = 10.0
    } else {
      modelTextScale.value = Math.max(0.1, Number(value))
    }

    modelTextScaleInputRef.value!.value = modelTextScale.value.toFixed(2)
  },
})

const emitTextScale = () => {
  emit('update-text-scale', modelTextScale.value)
}

const updateTextScale = debounce(emitTextScale, 250)

function calculateInitialSliderValue(modelTextScale: number) {
  let input

  if (modelTextScale <= 1) {
    input = ((modelTextScale - 0.1) / (1 - 0.1)) * 0.3 * 10
  } else {
    input = (((modelTextScale - 1) / (10 - 1)) * 0.7 + 0.3) * 10
  }

  return Math.max(0.1, input)
}

function handleModelTextScaleInput(input: number) {
  const max = 10
  const step = input / max
  let scaledValue: number

  if (input === 0.1) scaledValue = 0.1
  else if (step <= 0.3) {
    scaledValue = 0.1 + (step / 0.3) * (1 - 0.1)
  } else {
    scaledValue = 1 + ((step - 0.3) / 0.7) * (max - 1)
  }

  modelTextScale.value = parseFloat(scaledValue.toPrecision(3))
}

function handleInputBlur($event: FocusEvent) {
  computedModelTextScale.value = ($event.target as HTMLInputElement).value
}

watch(() => modelTextScale.value, updateTextScale)

watch(
  toRef(props, 'scopingElement'),
  newValue => {
    if (!newValue) {
      groupedLayers.value?.$el.querySelector('.selected')?.scrollIntoView({ block: 'center' })
    }
  },
  { flush: 'post' }
)

function forward(type: 'click' | 'contextmenu') {
  return function (e: Event, ...args: any[]): void {
    emit(type, e, ...args)
  }
}

function setLayerVisible(param: NodeVisibility[] | LabelVisibility[] | DrillLabelVisibility[]) {
  if (isNodeVisibility(param)) {
    props.threeDViewer.setLayerVisibility(param)
  } else if (isLabelVisibility(param)) {
    props.threeDViewer.labelsManager.setVisible(param[0].labelId, param[0].visible)
  } else if (isDrillLabelVisibility(param)) {
    param.forEach(({ drillLabelId, visible }) => {
      props.threeDViewer.world.drillDataSetManager?.drillLabelManager.setVisible(drillLabelId, visible)
    })
  }

  emit('set-layer-visible')
}

const forwardClick = forward('click')
const forwardContextmenu = forward('contextmenu')
</script>

<style lang="stylus" scoped>
:deep(.controls-container .control-button)
  padding calc(0.75rem - 1px) // Modify padding to have button size match slide buttons
  border-width 0
.text-scale-buttons
  @apply text-copper-800 bg-white rounded w-8 h-8
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button
  -webkit-appearance: none
  margin: 0
input[type="number"]
  -moz-appearance: textfield
  padding 1px 2px
</style>
